import {
  SetStateAction, useCallback, useEffect, useRef,
} from 'react';
import isEqual from 'lodash/isEqual';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { stockBulkFilterSelector } from '../../store/selectors/stockBulkSelector';
import { fetchStockBulkRequestItems } from '../../store/thunks/stock/stock-part-requests/stockBulkOperationsThunks';
import { PartRequestByLineFilters } from '../stock-part-requests/types/stockPartRequestTypes';
import { initPaging } from '../../core/types/coreTypes';

type SetFilterAction = (action: SetStateAction<PartRequestByLineFilters>) => void;
type ResetFilterAction = () => void;

const getFilterWithDefaults = (filter: PartRequestByLineFilters): PartRequestByLineFilters => ({
  keyword: '',
  isActive: true,
  statuses: [],
  linkedEntityNumber: '',
  linkedEntityStatus: '',
  productNumber: '',
  ...filter,
});

export const useBulkFilter = (): [PartRequestByLineFilters, SetFilterAction, ResetFilterAction] => {
  const dispatch = useAppDispatch();
  const filter = useAppSelector(stockBulkFilterSelector);

  const abortController = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  const dispatchFilter = useCallback((filter: PartRequestByLineFilters) => {
    abortController.current?.abort();
    abortController.current = new AbortController();
    dispatch(fetchStockBulkRequestItems({ filters: filter, signal: abortController.current?.signal }));
  }, [dispatch]);

  const setFilter: SetFilterAction = useCallback((action) => {
    dispatch((dispatchInternal, getState) => {
      const currentFilter = getFilterWithDefaults(stockBulkFilterSelector(getState()));
      const nextFilter = getFilterWithDefaults(typeof action === 'function' ? action(currentFilter) : action);
      const hasChanges = !isEqual(nextFilter, currentFilter);

      if (hasChanges) {
        dispatchFilter(nextFilter);
      }
    });
  }, [dispatch, dispatchFilter]);

  const resetFilter: ResetFilterAction = useCallback(() => {
    dispatch(() => {
      const currentFilter = getFilterWithDefaults(initPaging);
      dispatchFilter(getFilterWithDefaults(currentFilter));
    });
  }, [dispatch, dispatchFilter]);

  return [filter, setFilter, resetFilter];
};
